import React, { useEffect, useState } from 'react';
import StudentLayout from '../components/student/StudentLayout';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Divider,
  Box,
  InputBase,
  Avatar,
  CircularProgress,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import moduleTicketImg from '../img/ModuleImg.jpeg';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { db } from '../firebaseConfig';
import { signOut } from 'firebase/auth';
import { collection, query as fbQuery, where, getDocs, onSnapshot, doc, getDoc, orderBy, limit } from 'firebase/firestore';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useTranslation } from 'react-i18next';
import { getRoleLabels, getStoredTutorType } from '../utils/roleTerminology';
import ClassCard from '../components/student/ClassCard';
import { cardShadow } from '../theme/tokens';

const toStartMillis = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  const [y, m, d] = String(dateStr).split('/').map(Number);
  const [hh, mm] = String(timeStr).split(':').map(Number);
  const date = new Date(y, m - 1, d, hh, mm, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
};

const normalizeTimestamp = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  return 0;
};

const formatRelativeTime = (timestamp, t) => {
  const ms = normalizeTimestamp(timestamp);
  if (!ms) return '';
  const diffMinutes = Math.max(1, Math.floor((Date.now() - ms) / 60000));
  if (diffMinutes < 60) return t('{{count}}m ago', { count: diffMinutes });
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return t('{{count}}h ago', { count: diffHours });
  const diffDays = Math.floor(diffHours / 24);
  return t('{{count}}d ago', { count: diffDays });
};

const meetingAccentColors = ['#0878ef', '#10b981', '#f97316', '#0878ef'];

function StudentsClasses() {


    const { t } = useTranslation();
    const navigate = useNavigate();
    const [firstName, setFirstName] = useState('');
    const [classes, setClasses] = useState([]);
    const [classesLoading, setClassesLoading] = useState(true);
    const [error, setError] = useState('');
    const [msgCount, setMsgCount] = useState(0);
    const [classSearchText, setClassSearchText] = useState('');
    const [studentDocId, setStudentDocId] = useState('');
    const [upcomingMeetings, setUpcomingMeetings] = useState([]);
    const [recentMessages, setRecentMessages] = useState([]);
    const auth = getAuth();
    const role = getRoleLabels(t, getStoredTutorType());


  // realtime unread chats badge (updates instantly)
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setMsgCount(0);
      return;
    }

    let unsubStudent = null;
    let unsubConvos = null;

    // 1) watch student doc (because you said conversations.studentId stores studentDocId)
    const sQ = fbQuery(collection(db, "students"), where("uid", "==", user.uid));

    unsubStudent = onSnapshot(
      sQ,
      (sSnap) => {
        if (sSnap.empty) {
          setStudentDocId('');
          setMsgCount(0);
          if (unsubConvos) {
            unsubConvos();
            unsubConvos = null;
          }
          return;
        }

        const studentDocId = sSnap.docs[0].id;
        setStudentDocId(studentDocId);

        // 2) rebuild conversations listener whenever studentDocId is known/changes
        if (unsubConvos) {
          unsubConvos();
          unsubConvos = null;
        }

        const cQ = fbQuery(collection(db, "conversations"), where("studentId", "==", studentDocId));

        unsubConvos = onSnapshot(
          cQ,
          (cSnap) => {
            let total = 0;
            cSnap.forEach((d) => {
              const data = d.data() || {};
              total += Number(data.unreadCountStudent || 0);
            });
            setMsgCount(total);
          },
          (err) => {
            console.error("Conversations listener error:", err);
            setMsgCount(0);
          }
        );
      },
      (err) => {
        console.error("Student doc listener error:", err);
        setMsgCount(0);
      }
    );

    return () => {
      if (unsubConvos) unsubConvos();
      if (unsubStudent) unsubStudent();
    };
  }, []);

  useEffect(() => {
    if (!studentDocId) {
      setRecentMessages([]);
      return undefined;
    }

    const conversationsQuery = fbQuery(
      collection(db, "conversations"),
      where("studentId", "==", studentDocId),
      orderBy("lastMessageAt", "desc"),
      limit(4)
    );

    const unsubscribe = onSnapshot(
      conversationsQuery,
      (snapshot) => {
        const rows = snapshot.docs
          .map((conversationDoc) => {
            const data = conversationDoc.data() || {};
            return {
              id: conversationDoc.id,
              teacherName: data.teacherName || t("Teacher"),
              teacherPhotoURL: data.teacherPhotoURL || "",
              text: data.lastMessageType && data.lastMessageType !== "text"
                ? t("Attachment")
                : data.lastMessageText || t("No messages yet"),
              time: formatRelativeTime(data.lastMessageAt, t),
              unread: Number(data.unreadCountStudent || 0),
            };
          })
          .filter((message) => message.text && message.text !== t("No messages yet"));
        setRecentMessages(rows);
      },
      (err) => {
        console.error("Recent messages listener error:", err);
        setRecentMessages([]);
      }
    );

    return () => unsubscribe();
  }, [studentDocId, t]);


  // Fonction pour récupérer les groupes
  const fetchClasses = async () => {
    setClassesLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        setError(t('User not authenticated'));
        return;
      }

      const studentQuery = fbQuery(
        collection(db, 'students'),
        where('uid', '==', user.uid)
      );

      const querySnapshot = await getDocs(studentQuery);
      if (querySnapshot.empty) {
        setError(role.isCoach ? t('No client data found for the current user.') : t('No student data found for the current user.'));
        console.warn("No teacher data found. Signing out the user...");

        // Sign the user out
        await signOut(auth);
        return;
      }

      const studentData = querySnapshot.docs[0].data();
      const enrolledClasses = studentData.enrollments || [];

      const classesWithImages = await Promise.all(
        enrolledClasses.map(async (enrollment) => {
          const groupId = enrollment?.groupId;
          if (!groupId) return { ...enrollment, groupImage: '' };

          try {
            const groupRef = doc(db, 'groups', groupId);
            const groupSnap = await getDoc(groupRef);
            const groupData = groupSnap.exists() ? groupSnap.data() : {};
            const filesSnap = groupSnap.exists()
              ? await getDocs(collection(groupRef, 'files'))
              : { size: 0 };

            let teacherName = '';
            let teacherUid = '';
            const teacherId = groupData?.teacherId || enrollment?.teacherId || '';
            if (teacherId) {
              const teacherSnap = await getDoc(doc(db, 'teachers', teacherId));
              if (teacherSnap.exists()) {
                const teacherData = teacherSnap.data() || {};
                teacherName = `${teacherData.firstName || ''} ${teacherData.lastName || ''}`.trim();
                teacherUid = teacherData.uid || teacherId;
              } else {
                const teacherQuery = fbQuery(collection(db, 'teachers'), where('uid', '==', teacherId));
                const teacherQuerySnap = await getDocs(teacherQuery);
                if (!teacherQuerySnap.empty) {
                  const teacherData = teacherQuerySnap.docs[0].data() || {};
                  teacherName = `${teacherData.firstName || ''} ${teacherData.lastName || ''}`.trim();
                  teacherUid = teacherData.uid || teacherId;
                }
              }
            }

            return {
              ...enrollment,
              groupName: enrollment?.groupName || groupData?.name || '',
              groupImage: groupData?.groupImage || '',
              subjectId: groupData?.subjectId || enrollment?.subjectId || '',
              teacherName,
              teacherUid,
              fileCount: filesSnap.size || 0,
            };
          } catch (e) {
            console.error('Error fetching group details:', e);
            return { ...enrollment, groupImage: '' };
          }
        })
      );

      setClasses(classesWithImages);

      const meetingGroups = await Promise.all(
        classesWithImages.map(async (classData) => {
          if (!classData?.teacherUid || !classData?.groupId) return [];
          try {
            const meetingsSnap = await getDocs(collection(db, 'meetings', classData.teacherUid, classData.groupId));
            const items = [];
            meetingsSnap.forEach((meetingDoc) => {
              const data = meetingDoc.data() || {};
              const startMs = toStartMillis(data.date, data.startTime);
              if (!startMs || startMs < Date.now()) return;
              items.push({
                id: meetingDoc.id,
                groupId: classData.groupId,
                title: data.title || classData.groupName || t('Untitled meeting'),
                place: data.url ? t('Online') : (data.description || t('Meeting')),
                startTime: data.startTime || '--:--',
                startMs,
              });
            });
            return items;
          } catch (meetingError) {
            console.error('Error fetching upcoming meetings:', meetingError);
            return [];
          }
        })
      );

      setUpcomingMeetings(
        meetingGroups
          .flat()
          .sort((a, b) => a.startMs - b.startMs)
          .slice(0, 4)
      );
    } catch (err) {
      console.error('Error fetching classes:', err);
      setError(t('An error occurred while fetching classes.'));
    } finally {
      setClassesLoading(false);
    }
  };

  useEffect(() => {
    fetchClasses();
  }, []);


  // Fetch the current user's first name
  useEffect(() => {
     const fetchFirstName = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
          console.error('No user logged in.');
         return;
       }
        
       const userUID = currentUser.uid;
       try {
         const studentQuery = fbQuery(collection(db, 'students'), where('uid', '==', userUID));
         const querySnapshot = await getDocs(studentQuery);
        
         if (!querySnapshot.empty) {
           const studentData = querySnapshot.docs[0].data();
           setFirstName(studentData.firstName);
          } else {
            console.error('No matching student found.');
            // Sign the user out
            await signOut(auth);
            return;
         }
       } catch (error) {
         console.error('Error fetching student data:', error);
       }
      };
        
    fetchFirstName();
     }, []);


  const handleClassClick = (groupId) => {
    navigate(`/student/class/${groupId}`);
  };

  const normalizedClassSearch = classSearchText.trim().toLowerCase();
  const filteredClasses = normalizedClassSearch
    ? classes.filter((classData) =>
        [
          classData?.groupName,
          classData?.subjectId,
          classData?.teacherName,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedClassSearch))
      )
    : classes;

  const performanceItems = [
    { label: t('Mathematics'), value: '88%', color: '#4f86f7' },
    { label: t('Science'), value: '82%', color: '#10c9a7' },
    { label: t('English'), value: '79%', color: '#6b9cf6' },
    { label: t('Social Studies'), value: '85%', color: '#0688e8' },
  ];

  return (
    <StudentLayout>
      <Box
        sx={{
          maxWidth: 1280,
          mx: 'auto',
          width: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
        }}
      >
        {/* Learning Overview */}
        <Box
          sx={{
            mb: 2,
            pt: { xs: 0, md: 0.5 },
          }}
        >
          <Typography
            variant="h3"
            sx={{
              color: '#081a4a',
              fontWeight: 800,
              fontSize: { xs: 16, md: 20 },
              lineHeight: 1.12,
              letterSpacing: 0,
              mb: 0.75,
            }}
          >
            {t('Good morning, {{name}}', { name: firstName || t('Student') })}
          </Typography>
          <Typography
            sx={{
              color: '#49699f',
              fontSize: { xs: 10, md: 12 },
              fontWeight: 500,
            }}
          >
            {t("Here's your learning overview and what's happening in your classes.")}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
            gap: { xs: 1.5, md: 1.75 },
            mb: 2,
            minWidth: 0,
            width: '100%',
          }}
        >
          {[
            {
              icon: <MenuBookOutlinedIcon />,
              accent: '#4f46e5',
              bg: 'linear-gradient(135deg, rgba(124,58,237,0.13), rgba(47,111,237,0.09))',
              label: role.isCoach ? t('Total programs') : t('Total classes'),
              value: classes.length,
              helper: t('Across all subjects'),
            },
            {
              icon: <ChatBubbleOutlineOutlinedIcon />,
              accent: '#0878ef',
              bg: 'linear-gradient(135deg, rgba(8,120,239,0.13), rgba(8,120,239,0.06))',
              label: t('Unread messages'),
              value: msgCount,
              helper: t('From teachers & school'),
            },
            {
              icon: <SchoolOutlinedIcon />,
              accent: '#10b981',
              bg: 'linear-gradient(135deg, rgba(16,185,129,0.16), rgba(16,185,129,0.07))',
              label: role.isCoach ? t('Active programs') : t('Active enrollments'),
              value: classes.length,
              helper: t('Currently enrolled'),
            },
            {
              icon: <WorkspacePremiumOutlinedIcon />,
              accent: '#f97316',
              bg: 'linear-gradient(135deg, rgba(249,115,22,0.14), rgba(249,115,22,0.06))',
              label: t('Completed lessons'),
              value: 0,
              helper: t('Across all subjects'),
            },
          ].map((item) => (
            <Box
              key={item.label}
              sx={{
                bgcolor: '#fff',
                borderRadius: 1,
                border: '1px solid #dce8f8',
                boxShadow: '0 8px 20px rgba(8, 26, 74, 0.035)',
                px: { xs: 2, md: 1.75 },
                py: { xs: 1.75, md: 1.55 },
                minHeight: 92,
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1.75, md: 1.65 },
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: 2,
                  background: item.bg,
                  color: item.accent,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                  '& svg': { fontSize: 28 },
                }}
              >
                {item.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    color: '#081a4a',
                    fontWeight: 800,
                    fontSize: 13.5,
                    lineHeight: 1.25,
                    mb: 0.55,
                  }}
                >
                  {item.label}
                </Typography>
                <Typography
                  sx={{
                    color: '#081a4a',
                    fontWeight: 900,
                    fontSize: 26,
                    lineHeight: 1,
                    mb: 0.5,
                  }}
                >
                  {item.value}
                </Typography>
                <Typography sx={{ color: '#49699f', fontSize: 12.5, fontWeight: 500 }}>
                  {item.helper}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr)',
            gap: 3,
            minWidth: 0,
            width: '100%',
          }}
        >
          {/* Left Column */}
          <Box sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
            {/* My Classes Overview */}
            <Box
              sx={{
                bgcolor: '#fff',
                borderRadius: 1,
                p: { xs: 1.5, md: 1.75 },
                border: '1px solid #eef1f6',
                boxShadow: cardShadow,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: 2,
                  mb: 2.5,
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#081a4a', fontSize: { xs: 16, md: 17 } }}>
                  {role.isCoach ? t('My Programs') : t('My Classes')}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: { xs: '100%', sm: 360 },
                    maxWidth: '100%',
                    px: 1.5,
                    py: 0.45,
                    borderRadius: 1,
                    bgcolor: '#fff',
                    border: '1px solid #dce8f8',
                    color: '#6f89b8',
                    boxShadow: '0 4px 12px rgba(8, 26, 74, 0.025)',
                  }}
                >
                  <InputBase
                    placeholder={role.isCoach ? t('Search my programs...') : t('Search my classes...')}
                    value={classSearchText}
                    onChange={(event) => setClassSearchText(event.target.value)}
                    sx={{ color: '#081a4a', width: '100%', fontSize: 13.25, fontWeight: 500, lineHeight: 1.2 }}
                  />
                  <SearchRoundedIcon sx={{ fontSize: 20, color: '#6f89b8', flexShrink: 0 }} />
                </Box>
              </Box>
              <Divider sx={{ display: 'none' }} />
              {classesLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 6 }}>
                  <CircularProgress size={32} sx={{ color: '#0878ef' }} />
                  <Typography sx={{ color: '#64748b', fontSize: 13.5, fontWeight: 500 }}>
                    {role.isCoach ? t('Searching for your programs...') : t('Searching for your classes...')}
                  </Typography>
                </Box>
              ) : error ? (
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              ) : classes.length === 0 ? (
                <Typography sx={{ color: '#64748b' }}>
                  {role.isCoach ? t('No programs assigned yet.') : t('No classes assigned yet.')}
                </Typography>
              ) : filteredClasses.length === 0 ? (
                <Typography sx={{ color: '#64748b' }}>
                  {role.isCoach ? t('No programs match your search.') : t('No classes match your search.')}
                </Typography>
              ) : (
                <Box
                  id="my-classes"
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
                    gap: { xs: 1.75, md: 2.25 },
                    minWidth: 0,
                  }}
                >
                  {filteredClasses.map((classData) => (
                    <ClassCard
                      key={classData.groupId}
                      image={classData.groupImage}
                      fallbackImage={moduleTicketImg}
                      title={classData.groupName}
                      subjectId={classData.subjectId}
                      teacherName={classData.teacherName}
                      fileCount={classData.fileCount}
                      onView={() => handleClassClick(classData.groupId)}
                      viewLabel={role.isCoach ? t('Open Program') : t('Open Class')}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                md: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 1.5,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                bgcolor: '#fff',
                borderRadius: 1,
                p: 1.35,
                border: '1px solid #dce8f8',
                boxShadow: '0 8px 20px rgba(8, 26, 74, 0.035)',
                minHeight: 188,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.85 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9 }}>
                  <CalendarMonthOutlinedIcon sx={{ color: '#0878ef', fontSize: 17 }} />
                  <Typography sx={{ color: '#081a4a', fontWeight: 800, fontSize: 12.5 }}>
                    {t('Upcoming Meetings')}
                  </Typography>
                </Box>
                <Typography sx={{ color: '#006df0', fontWeight: 800, fontSize: 10.75 }}>
                  {t('View all')}
                </Typography>
              </Box>
              {upcomingMeetings.length === 0 ? (
                <Box sx={{ height: 132, display: 'grid', placeItems: 'center', textAlign: 'center', px: 2 }}>
                  <Box>
                    <CalendarMonthOutlinedIcon sx={{ color: '#8aa4ca', fontSize: 28, mb: 0.6 }} />
                    <Typography sx={{ color: '#081a4a', fontSize: 11.5, fontWeight: 800 }}>
                      {t('No upcoming meetings')}
                    </Typography>
                    <Typography sx={{ color: '#6f89b8', fontSize: 10.25, fontWeight: 600, mt: 0.35 }}>
                      {t('Scheduled sessions will appear here.')}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'grid' }}>
                  {upcomingMeetings.map((meeting, index) => (
                    <Box
                      key={`${meeting.groupId}-${meeting.id}`}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '70px minmax(0, 1fr)',
                        py: 0.72,
                        borderBottom: index === upcomingMeetings.length - 1 ? 'none' : '1px solid #eef3fb',
                        minWidth: 0,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9 }}>
                        <Box sx={{ width: 2.5, height: 29, borderRadius: 2, bgcolor: meetingAccentColors[index % meetingAccentColors.length] }} />
                        <Typography sx={{ color: '#49699f', fontSize: 10.5, fontWeight: 600 }}>
                          {meeting.startTime}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ color: '#081a4a', fontWeight: 800, fontSize: 10.75, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {meeting.title}
                        </Typography>
                        <Typography sx={{ color: '#006df0', fontWeight: 700, fontSize: 10.25, mt: 0.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {meeting.place}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Box
              sx={{
                bgcolor: '#fff',
                borderRadius: 1,
                p: 1.35,
                border: '1px solid #dce8f8',
                boxShadow: '0 8px 20px rgba(8, 26, 74, 0.035)',
                minHeight: 188,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.9 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9 }}>
                  <ChatBubbleOutlineOutlinedIcon sx={{ color: '#0878ef', fontSize: 17 }} />
                  <Typography sx={{ color: '#081a4a', fontWeight: 800, fontSize: 12.5 }}>
                    {t('Received Messages')}
                  </Typography>
                </Box>
                <Typography sx={{ color: '#006df0', fontWeight: 800, fontSize: 10.75 }}>
                  {t('View all')}
                </Typography>
              </Box>
              {recentMessages.length === 0 ? (
                <Box sx={{ height: 132, display: 'grid', placeItems: 'center', textAlign: 'center', px: 2 }}>
                  <Box>
                    <ChatBubbleOutlineOutlinedIcon sx={{ color: '#8aa4ca', fontSize: 28, mb: 0.6 }} />
                    <Typography sx={{ color: '#081a4a', fontSize: 11.5, fontWeight: 800 }}>
                      {t('No received messages')}
                    </Typography>
                    <Typography sx={{ color: '#6f89b8', fontSize: 10.25, fontWeight: 600, mt: 0.35 }}>
                      {t('Teacher messages will appear here.')}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'grid', gap: 0.75 }}>
                  {recentMessages.map((message) => (
                    <Box key={message.id} sx={{ display: 'grid', gridTemplateColumns: '31px minmax(0, 1fr) auto 6px', alignItems: 'center', gap: 0.75 }}>
                      {message.teacherPhotoURL ? (
                        <Avatar src={message.teacherPhotoURL} sx={{ width: 29, height: 29 }} />
                      ) : (
                        <Avatar sx={{ width: 29, height: 29, bgcolor: '#5b8df7', fontSize: 11, fontWeight: 800 }}>
                          {message.teacherName?.slice(0, 1) || <DescriptionOutlinedIcon sx={{ fontSize: 15 }} />}
                        </Avatar>
                      )}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ color: '#081a4a', fontWeight: 800, fontSize: 10.75, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {message.teacherName}
                        </Typography>
                        <Typography sx={{ color: '#49699f', fontWeight: 600, fontSize: 9.25, mt: 0.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {message.text}
                        </Typography>
                      </Box>
                      <Typography sx={{ color: '#49699f', fontWeight: 600, fontSize: 9.5, whiteSpace: 'nowrap' }}>
                        {message.time}
                      </Typography>
                      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: message.unread > 0 ? '#0878ef' : 'transparent' }} />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Box
              sx={{
                bgcolor: '#fff',
                borderRadius: 1,
                p: 1.35,
                border: '1px solid #dce8f8',
                boxShadow: '0 8px 20px rgba(8, 26, 74, 0.035)',
                minHeight: 188,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography sx={{ color: '#081a4a', fontWeight: 800, fontSize: 12.5 }}>
                  {t("Student's AI Interactions")}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: '#49699f' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 9.75 }}>{t('This Month')}</Typography>
                  <KeyboardArrowDownRoundedIcon sx={{ fontSize: 15 }} />
                </Box>
              </Box>
              <Box sx={{ height: 146, position: 'relative', color: '#49699f', fontSize: 10, fontWeight: 600 }}>
                {[100, 75, 50, 25, 0].map((value, index) => (
                  <Box key={value} sx={{ position: 'absolute', left: 0, right: 0, top: `${index * 23}%`, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ width: 22, color: '#49699f', fontSize: 9.75 }}>{value}</Typography>
                    <Box sx={{ flex: 1, borderTop: '1px solid #e8eff8' }} />
                  </Box>
                ))}
                <Box sx={{ position: 'absolute', left: 31, right: 3, top: 7, bottom: 28 }}>
                  <svg viewBox="0 0 300 128" width="100%" height="100%" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="aiArea" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0878ef" stopOpacity="0.16" />
                        <stop offset="100%" stopColor="#0878ef" stopOpacity="0.02" />
                      </linearGradient>
                    </defs>
                    <path d="M0 96 L35 94 L75 68 L112 52 L150 68 L196 26 L244 55 L286 16 L300 0 L300 128 L0 128 Z" fill="url(#aiArea)" />
                    <polyline points="0,96 35,94 75,68 112,52 150,68 196,26 244,55 286,16 300,0" fill="none" stroke="#0878ef" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    {[['0','96'],['35','94'],['75','68'],['112','52'],['150','68'],['196','26'],['244','55'],['286','16'],['300','0']].map(([cx, cy]) => (
                      <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="4" fill="#0878ef" />
                    ))}
                  </svg>
                </Box>
                <Box sx={{ position: 'absolute', left: 31, right: 3, bottom: 9, display: 'flex', justifyContent: 'space-between' }}>
                  {[t('May 1'), t('May 8'), t('May 15'), t('May 22'), t('May 29')].map((label) => (
                    <Typography key={label} sx={{ color: '#49699f', fontSize: 9.25, fontWeight: 600 }}>{label}</Typography>
                  ))}
                </Box>
                <Box sx={{ position: 'absolute', bottom: -5, left: 0, right: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 20, height: 2, bgcolor: '#0878ef', position: 'relative', '&::after': { content: '""', position: 'absolute', width: 6, height: 6, borderRadius: '50%', bgcolor: '#0878ef', left: 7, top: -2 } }} />
                  <Typography sx={{ color: '#49699f', fontSize: 9.75, fontWeight: 600 }}>{t('AI Interactions')}</Typography>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                bgcolor: '#fff',
                borderRadius: 1,
                p: 1.35,
                border: '1px solid #dce8f8',
                boxShadow: '0 8px 20px rgba(8, 26, 74, 0.035)',
                minHeight: 188,
              }}
            >
              <Typography sx={{ color: '#081a4a', fontWeight: 800, fontSize: 12.5, mb: 1.25 }}>
                {t('Student Performance')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '0.9fr 1fr', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 112,
                    height: 112,
                    mx: 'auto',
                    borderRadius: '50%',
                    background: 'conic-gradient(#10c9a7 0 25%, #0688e8 25% 55%, #ffa640 55% 68%, #8157ea 68% 76%, #4f86f7 76% 100%)',
                    display: 'grid',
                    placeItems: 'center',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: '#fff',
                      position: 'absolute',
                    },
                  }}
                >
                  <Box sx={{ position: 'relative', textAlign: 'center' }}>
                    <Typography sx={{ color: '#49699f', fontSize: 9.75, fontWeight: 700 }}>{t('Overall')}</Typography>
                    <Typography sx={{ color: '#081a4a', fontSize: 20, fontWeight: 900, lineHeight: 1 }}>84%</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'grid', gap: 1.05, minWidth: 0 }}>
                  {performanceItems.map((item) => (
                    <Box key={item.label} sx={{ display: 'grid', gridTemplateColumns: '10px minmax(0, 1fr) auto', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 6.5, height: 6.5, borderRadius: '50%', bgcolor: item.color }} />
                      <Typography sx={{ color: '#49699f', fontSize: 10.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {item.label}
                      </Typography>
                      <Typography sx={{ color: '#081a4a', fontSize: 10.5, fontWeight: 900 }}>{item.value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Typography sx={{ color: '#49699f', fontSize: 9.75, fontWeight: 600, textAlign: 'center', mt: 1.15 }}>
                {t('Based on assessments & assignments')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

    </StudentLayout>
  );
}

export default StudentsClasses;
